import React, { useEffect, useState } from "react";
import {
    Shield, KeyRound, RefreshCw, Copy, ExternalLink, AlertTriangle,
    Plus, Trash2, Save, FileCode, Check,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";

/**
 * SsoSettings — admin-only SAML 2.0 + SCIM 2.0 configuration.
 *
 * Layout: 4 stacked cards (SP-side info, IdP-side config, group→role
 * mapping, SCIM token). Each card is self-contained and persists via
 * the same PUT /api/sso (except SCIM token rotation which uses its own
 * endpoint because the raw token is only emitted at rotation time).
 */
export default function SsoSettings({ fullWidth = false }) {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);
    const [rotating, setRotating] = useState(false);
    const [parseBusy, setParseBusy] = useState(false);
    const [metadataXml, setMetadataXml] = useState("");
    const [parsedPreview, setParsedPreview] = useState(null);
    const [revealedToken, setRevealedToken] = useState(null);

    const refresh = async () => {
        try {
            const { data } = await http.get("/sso");
            setData(data);
        } catch (e) {
            toast.error(errMsg(e));
        }
    };
    useEffect(() => { refresh(); }, []);

    const save = async () => {
        if (!data) return;
        setSaving(true);
        try {
            await http.put("/sso", {
                saml: data.saml,
                scim: { enabled: data.scim.enabled || data.scim.has_token },
            });
            toast.success("SSO settings saved");
            await refresh();
        } catch (e) {
            toast.error(errMsg(e));
        } finally { setSaving(false); }
    };

    const parseMetadata = async () => {
        if (!metadataXml.trim()) { toast.error("Paste metadata XML first"); return; }
        setParseBusy(true);
        try {
            const r = await http.post("/auth/saml/parse-metadata", { xml: metadataXml });
            setParsedPreview(r.data);
            // Auto-fill the live config from the parse result.
            setData((d) => ({
                ...d,
                saml: {
                    ...d.saml,
                    idp_entity_id: r.data.idp_entity_id || d.saml.idp_entity_id,
                    idp_sso_url: r.data.idp_sso_url || d.saml.idp_sso_url,
                    idp_slo_url: r.data.idp_slo_url || d.saml.idp_slo_url,
                    idp_x509certs: r.data.idp_x509certs?.length ? r.data.idp_x509certs : d.saml.idp_x509certs,
                },
            }));
            toast.success("Metadata parsed and applied — review then click Save");
        } catch (e) { toast.error(errMsg(e)); }
        finally { setParseBusy(false); }
    };

    const rotateToken = async () => {
        if (!window.confirm("Rotating will invalidate the existing SCIM token immediately. Continue?")) return;
        setRotating(true);
        try {
            const r = await http.post("/sso/scim/rotate-token");
            setRevealedToken(r.data.token);
            toast.success("New SCIM token issued — copy it now, you won't see it again");
            await refresh();
        } catch (e) { toast.error(errMsg(e)); }
        finally { setRotating(false); }
    };

    const revokeToken = async () => {
        if (!window.confirm("Revoke the SCIM token? This will disable SCIM provisioning.")) return;
        try {
            await http.delete("/sso/scim/revoke");
            setRevealedToken(null);
            toast.success("SCIM token revoked");
            await refresh();
        } catch (e) { toast.error(errMsg(e)); }
    };

    const copy = (text, label) => {
        navigator.clipboard.writeText(text);
        toast.success(`${label} copied`);
    };

    if (!data) {
        return <div className="p-8 text-sm text-muted-foreground">Loading SSO settings…</div>;
    }

    const setSaml = (patch) => setData({ ...data, saml: { ...data.saml, ...patch } });

    return (
        <div className={`mx-auto space-y-6 p-6 ${fullWidth ? "max-w-none" : "max-w-[1100px]"}`} data-testid="sso-settings-page">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Single Sign-On
                    </div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">SSO &amp; SCIM</h1>
                    <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                        SAML 2.0 + SCIM 2.0 — connect Okta, Microsoft Entra ID, Google Workspace, or any standards-compliant IdP.
                        After saving, paste your <span className="font-mono">SP metadata URL</span> into the IdP, map at least one group, and try the SSO login.
                    </p>
                </div>
                <button onClick={save} disabled={saving} data-testid="sso-save"
                        className="inline-flex items-center gap-2 rounded-md bg-gradient-to-r from-[#1D4ED8] to-[#1E40AF] px-5 py-2.5 text-sm font-semibold text-white shadow hover:brightness-110 disabled:opacity-50">
                    <Save size={14} /> {saving ? "Saving…" : "Save settings"}
                </button>
            </header>

            {/* ── 1. Service Provider info (read-only, copy-able) ───── */}
            <Card icon={Shield} title="MoovBytes SP details" subtitle="Copy these into your IdP when registering a new SAML application.">
                <CopyRow label="SP metadata URL" value={data.sp_metadata_url} onCopy={copy} testid="sso-sp-metadata"
                         extra={<a href={data.sp_metadata_url} target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 text-[11px] text-[#1D4ED8] hover:underline"
                             data-testid="sso-sp-metadata-open"><ExternalLink size={11}/> Open</a>} />
                <CopyRow label="ACS (assertion consumer)" value={data.sp_acs_url} onCopy={copy} testid="sso-sp-acs" />
                <CopyRow label="Login start URL" value={data.login_url} onCopy={copy} testid="sso-login-url" />
                <CopyRow label="SCIM base URL" value={data.scim_base_url} onCopy={copy} testid="sso-scim-base" />
                <CopyRow label="Tenant slug" value={data.tenant_slug} onCopy={copy} testid="sso-tenant-slug" />
            </Card>

            {/* ── 2. SAML config (paste IdP metadata or manual) ─────── */}
            <Card icon={KeyRound} title="SAML Identity Provider" subtitle="Paste your IdP's metadata XML and we'll auto-fill the rest. Toggle Enabled to activate.">
                <div className="mb-4 flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2">
                    <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
                        <input type="checkbox" checked={data.saml.enabled}
                               onChange={(e) => setSaml({ enabled: e.target.checked })}
                               data-testid="sso-saml-enabled"
                               className="h-4 w-4 accent-[#1D4ED8]" />
                        SAML SSO enabled
                    </label>
                    <span className="text-[11px] text-muted-foreground">
                        While off, only password login is accepted.
                    </span>
                </div>

                <details className="mb-4 rounded-md border border-border bg-background">
                    <summary className="flex cursor-pointer items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <FileCode size={12} /> Paste IdP metadata XML
                    </summary>
                    <div className="space-y-2 border-t border-border p-3">
                        <textarea value={metadataXml} onChange={(e) => setMetadataXml(e.target.value)}
                                  rows={6} placeholder="<?xml version='1.0'?> <EntityDescriptor …"
                                  data-testid="sso-metadata-xml"
                                  className="w-full rounded-md border border-input bg-background p-2 font-mono text-[11px]" />
                        <div className="flex items-center justify-between">
                            <button onClick={parseMetadata} disabled={parseBusy} data-testid="sso-parse-metadata"
                                    className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-background hover:opacity-90 disabled:opacity-50">
                                <FileCode size={11} /> {parseBusy ? "Parsing…" : "Parse & apply"}
                            </button>
                            {parsedPreview && (
                                <div className="text-[11px] text-[#10b981]">
                                    <Check size={11} className="mr-1 inline"/> {parsedPreview.idp_x509certs?.length || 0} cert(s), fp: {(parsedPreview.cert_fingerprints?.[0] || "").slice(0, 16)}…
                                </div>
                            )}
                        </div>
                    </div>
                </details>

                <div className="grid gap-3 sm:grid-cols-2">
                    <Field label="IdP entity ID" testid="sso-idp-entity">
                        <input value={data.saml.idp_entity_id} onChange={(e) => setSaml({ idp_entity_id: e.target.value })}
                               placeholder="http://www.okta.com/exk123abc" className={inputCls} data-testid="sso-idp-entity-input" />
                    </Field>
                    <Field label="IdP SSO URL" testid="sso-idp-sso">
                        <input value={data.saml.idp_sso_url} onChange={(e) => setSaml({ idp_sso_url: e.target.value })}
                               placeholder="https://acme.okta.com/app/exk123/sso/saml" className={inputCls} data-testid="sso-idp-sso-input" />
                    </Field>
                    <Field label="IdP SLO URL (optional)" testid="sso-idp-slo">
                        <input value={data.saml.idp_slo_url} onChange={(e) => setSaml({ idp_slo_url: e.target.value })}
                               className={inputCls} data-testid="sso-idp-slo-input" />
                    </Field>
                    <Field label="Default role" testid="sso-default-role">
                        <select value={data.saml.default_role} onChange={(e) => setSaml({ default_role: e.target.value })}
                                className={inputCls} data-testid="sso-default-role-select">
                            {["viewer", "engineer", "auditor", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                    </Field>
                </div>

                <div className="mt-3">
                    <Field label={`IdP signing certificate${data.saml.idp_x509certs.length > 1 ? "s" : ""} — PEM`} testid="sso-idp-certs">
                        <textarea value={data.saml.idp_x509certs.join("\n\n")}
                                  onChange={(e) => setSaml({ idp_x509certs: e.target.value.split(/\n\n+/).map(s => s.trim()).filter(Boolean) })}
                                  rows={4}
                                  placeholder="-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----"
                                  data-testid="sso-idp-certs-input"
                                  className="w-full rounded-md border border-input bg-background p-2 font-mono text-[11px]" />
                    </Field>
                </div>
            </Card>

            {/* ── 3. Group → Role mapping ──────────────────────────── */}
            <Card icon={Shield} title="Group → Role mapping" subtitle="Map IdP group claim values to MoovBytes roles. First match wins; everyone else gets the default role.">
                <div className="space-y-2">
                    {Object.entries(data.saml.group_role_mapping).map(([group, role], i) => (
                        <div key={i} className="flex gap-2" data-testid={`sso-group-row-${i}`}>
                            <input value={group} placeholder="MoovBytes-Admins"
                                   onChange={(e) => {
                                       const m = { ...data.saml.group_role_mapping };
                                       delete m[group];
                                       m[e.target.value] = role;
                                       setSaml({ group_role_mapping: m });
                                   }}
                                   data-testid={`sso-group-name-${i}`}
                                   className={inputCls + " flex-1 font-mono"} />
                            <select value={role}
                                    onChange={(e) => setSaml({ group_role_mapping: { ...data.saml.group_role_mapping, [group]: e.target.value } })}
                                    data-testid={`sso-group-role-${i}`}
                                    className={inputCls + " w-36"}>
                                {["viewer", "engineer", "auditor", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                            <button onClick={() => {
                                const m = { ...data.saml.group_role_mapping }; delete m[group];
                                setSaml({ group_role_mapping: m });
                            }} data-testid={`sso-group-remove-${i}`}
                                    className="rounded-md border border-border px-2 text-muted-foreground hover:border-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]">
                                <Trash2 size={13} />
                            </button>
                        </div>
                    ))}
                    <button onClick={() => setSaml({ group_role_mapping: { ...data.saml.group_role_mapping, "": "viewer" } })}
                            data-testid="sso-group-add"
                            className="mt-1 inline-flex items-center gap-1.5 rounded-md border border-dashed border-border bg-background px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:border-foreground/40 hover:text-foreground">
                        <Plus size={11} /> Add mapping
                    </button>
                </div>
            </Card>

            {/* ── 4. SCIM token ────────────────────────────────────── */}
            <Card icon={KeyRound} title="SCIM 2.0 bearer token"
                  subtitle="Issued once at rotation — paste it into your IdP's SCIM provisioning settings. We store only its SHA-256 hash.">
                <div className="flex flex-wrap items-center gap-2">
                    {data.scim.has_token ? (
                        <>
                            <span className="mb-pill border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981]" data-testid="sso-scim-status-active">
                                <Check size={11} /> Token active · last rotated {(data.scim.last_rotated_at || "").slice(0, 10)}
                            </span>
                            <button onClick={rotateToken} disabled={rotating} data-testid="sso-scim-rotate"
                                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:border-[#1D4ED8]/50 hover:text-[#1D4ED8] disabled:opacity-50">
                                <RefreshCw size={11} /> {rotating ? "Rotating…" : "Rotate"}
                            </button>
                            <button onClick={revokeToken} data-testid="sso-scim-revoke"
                                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:border-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))]">
                                <Trash2 size={11} /> Revoke
                            </button>
                        </>
                    ) : (
                        <>
                            <span className="mb-pill border-amber-400/40 bg-amber-500/10 text-amber-300" data-testid="sso-scim-status-inactive">
                                <AlertTriangle size={11} /> No token issued
                            </span>
                            <button onClick={rotateToken} disabled={rotating} data-testid="sso-scim-issue"
                                    className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3.5 py-1.5 text-xs font-semibold uppercase tracking-widest text-background hover:opacity-90 disabled:opacity-50">
                                <KeyRound size={11} /> {rotating ? "Issuing…" : "Issue token"}
                            </button>
                        </>
                    )}
                </div>
                {revealedToken && (
                    <div className="mt-3 rounded-md border border-amber-400/40 bg-amber-500/5 p-3" data-testid="sso-scim-revealed">
                        <div className="mb-1 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-amber-300">
                            <AlertTriangle size={11} /> Copy this now — it won't be shown again
                        </div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 break-all rounded bg-background/60 px-2 py-1.5 font-mono text-[11px]" data-testid="sso-scim-revealed-token">
                                {revealedToken}
                            </code>
                            <button onClick={() => { copy(revealedToken, "SCIM token"); setRevealedToken(null); }}
                                    data-testid="sso-scim-copy-token"
                                    className="inline-flex items-center gap-1 rounded-md bg-foreground px-3 py-1.5 text-xs font-semibold text-background hover:opacity-90">
                                <Copy size={11} /> Copy &amp; hide
                            </button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}

// ── Sub-components ────────────────────────────────────────────────────

function Card({ icon: Icon, title, subtitle, children }) {
    return (
        <div className="rounded-xl border border-border bg-surface p-5" data-testid={`sso-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="mb-3 flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-[#1D4ED8]/20 to-[#1E40AF]/20 text-[#1D4ED8]">
                    <Icon size={16} />
                </div>
                <div>
                    <h2 className="font-display text-base font-bold leading-tight">{title}</h2>
                    {subtitle && <p className="text-[11px] text-muted-foreground">{subtitle}</p>}
                </div>
            </div>
            <div className="mt-3">{children}</div>
        </div>
    );
}

function CopyRow({ label, value, onCopy, extra, testid }) {
    return (
        <div className="flex flex-wrap items-center gap-2 border-t border-border/60 py-2 first:border-t-0 first:pt-0">
            <span className="w-44 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
            <code className="flex-1 truncate rounded bg-background/60 px-2 py-1 font-mono text-[11px]" data-testid={`${testid}-value`} title={value}>
                {value}
            </code>
            <button onClick={() => onCopy(value, label)} data-testid={`${testid}-copy`}
                    className="rounded-md border border-border bg-background p-1.5 text-muted-foreground hover:border-foreground/40 hover:text-foreground">
                <Copy size={12} />
            </button>
            {extra}
        </div>
    );
}

function Field({ label, children, testid }) {
    return (
        <label className="block" data-testid={testid}>
            <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

const inputCls =
    "w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-[#1D4ED8] focus:outline-none focus:ring-1 focus:ring-[#1D4ED8]/30";
